import { useState } from "react";
import { reissueGfToken, updateGfDeal } from "../../../../api/giftingClient";
import type { GfDealDetail } from "../../../../types/gifting";
import { errMsg } from "../../gfUtil";
import { ConfirmModal } from "../../modals/ConfirmModal";
import { GfButton, GfTag, copyText } from "../../ui/GfUi";

/** 本人入力フォームのURL（コピー・再発行・無効化）。トークン=認証そのものなので、再発行/無効化は確認付き */
export function FormUrlBox({
  detail,
  onChanged,
  toast,
}: {
  detail: GfDealDetail;
  onChanged: () => Promise<void>;
  toast: (m: string) => void;
}) {
  const [confirm, setConfirm] = useState<"reissue" | "revoke" | null>(null);
  const [busy, setBusy] = useState(false);
  const revoked = detail.token_revoked === 1;

  const copy = async () => {
    if (!detail.form_url) return;
    toast((await copyText(detail.form_url)) ? "フォームURLをコピーしました" : "コピーできませんでした。URLを選択して手動でコピーしてください");
  };

  const run = async (kind: "reissue" | "revoke") => {
    setBusy(true);
    try {
      if (kind === "reissue") {
        await reissueGfToken(detail.id);
        toast("フォームURLを再発行しました（以前のURLは使えません）");
      } else {
        await updateGfDeal(detail.id, { token_revoked: 1 });
        toast("フォームURLを無効化しました");
      }
      setConfirm(null);
      await onChanged();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="gf-panel-section">
      <div className="gf-panel-section-title">本人入力フォーム</div>
      <div className="gf-boxed gf-urlbox">
        <span className="gf-urlbox-url gf-mono">{detail.form_url ?? "URLがありません"}</span>
        {revoked && <GfTag tone="warn">無効</GfTag>}
      </div>
      <div className="gf-actions">
        <GfButton size="sm" disabled={!detail.form_url || revoked} onClick={() => void copy()}>
          コピー
        </GfButton>
        <GfButton size="sm" onClick={() => setConfirm("reissue")}>
          再発行
        </GfButton>
        <GfButton size="sm" variant="danger" disabled={revoked} onClick={() => setConfirm("revoke")}>
          無効化
        </GfButton>
      </div>
      <ConfirmModal
        open={confirm === "reissue"}
        title="フォームURLを再発行します"
        message="新しいURLが発行され、以前のURLは使えなくなります。クリエイターへ送り直してください。"
        confirmLabel="再発行する"
        busy={busy}
        onConfirm={() => void run("reissue")}
        onClose={() => setConfirm(null)}
      />
      <ConfirmModal
        open={confirm === "revoke"}
        title="フォームURLを無効化します"
        message="このURLからは住所や投稿を送れなくなります。必要なら「再発行」で復活できます。"
        confirmLabel="無効化する"
        danger
        busy={busy}
        onConfirm={() => void run("revoke")}
        onClose={() => setConfirm(null)}
      />
    </section>
  );
}
